/**
 * Three-Layer Feature Gate
 * 
 * Resolves whether a feature is enabled across system, company, and user layers.
 * The most restrictive layer wins: system OFF beats everything, company OFF beats user.
 * 
 * See: docs/features/infrastructure/feature-access-matrix.md#three-layer-feature-gate
 * 
 * Usage:
 *   const { enabled, reason } = resolveFeatureFlag('aiAssistant', {
 *     systemConfig,
 *     companyDoc,
 *     userSettings,
 *     userSettingKey: 'useAiAssistant'
 *   });
 * 
 * Reason values:
 *   'system'   - System gate is off → hide toggle entirely
 *   'company'  - Company gate is off → hide toggle entirely
 *   'role'     - User does not meet the effective minimum role
 *   'user-missing' - No personal preference has ever been stored
 *   'user-off' - Gates open but user hasn't opted in → show toggle (off)
 *   'user'     - All layers agree → feature is active
 */

import { meetsMinRole } from '../config/featureFlagDefinitions';

/**
 * Resolve whether a feature is enabled across all three layers.
 * @param {string} flagName - Key in featureFlags maps (e.g., 'aiAssistant')
 * @param {object} options
 * @param {object} options.systemConfig - globalSettings/systemConfig document
 * @param {object} options.companyDoc - companies/{companyId} document
 * @param {object} options.userSettings - users/{uid}.settings object
 * @param {string} options.userSettingKey - User's preference key (e.g., 'useAiAssistant')
 * @param {string} [options.userRole] - User's current role
 * @param {string|null} [options.minRole] - Effective company/default minimum role
 * @param {boolean} [options.hasSystemGate=true] - Whether this feature has a system switch
 * @returns {{ enabled: boolean, reason: 'system' | 'company' | 'role' | 'user-missing' | 'user-off' | 'user', preferenceState?: 'missing' | 'off' | 'on' }}
 */
export function resolveFeatureFlag(flagName, {
    systemConfig,
    companyDoc,
    userSettings,
    userSettingKey,
    userRole,
    minRole,
    hasSystemGate = true,
}) {
    // Layer 1: System — global kill switch
    if (hasSystemGate && systemConfig?.featureFlags?.[flagName] !== true) {
        return { enabled: false, reason: 'system' };
    }

    // Layer 2: Company — org-level gate
    if (companyDoc?.featureFlags?.[flagName] !== true) {
        return { enabled: false, reason: 'company' };
    }

    // Role gate — company override or feature-definition default
    if (!meetsMinRole(userRole, minRole)) {
        return { enabled: false, reason: 'role' };
    }

    // Layer 3: User — personal preference (opt-in)
    const userValue = userSettings?.[userSettingKey];
    if (userValue === undefined) {
        return { enabled: false, reason: 'user-missing', preferenceState: 'missing' };
    }
    if (userValue !== true) {
        return { enabled: false, reason: 'user-off', preferenceState: 'off' };
    }
    return { enabled: true, reason: 'user', preferenceState: 'on' };
}

/**
 * Find active company users who pass a feature's role gate but have never had
 * its personal preference stored. Explicit false values are intentionally not
 * included so rollout tools cannot overwrite an opt-out.
 */
export function findMissingFeaturePreferenceUsers(users, {
    companyId,
    userSettingKey,
    minRole,
    includeDisabled = false,
}) {
    return (users || []).filter((user) => (
        user.companyId === companyId
        && (includeDisabled || user.disabled !== true)
        && meetsMinRole(user.userRole || 'standard', minRole)
        && user.settings?.[userSettingKey] === undefined
    ));
}

/**
 * Check if a feature toggle should be visible to the user in Settings.
 * Returns true only when system and company gates are both open.
 * @param {string} flagName - Key in featureFlags maps
 * @param {object} options
 * @param {object} options.systemConfig - globalSettings/systemConfig document
 * @param {object} options.companyDoc - companies/{companyId} document
 * @returns {boolean}
 */
export function isFeatureToggleVisible(flagName, { systemConfig, companyDoc, hasSystemGate = true }) {
    return (
        (!hasSystemGate || systemConfig?.featureFlags?.[flagName] === true) &&
        companyDoc?.featureFlags?.[flagName] === true
    );
}
